import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TransactionActionsMenu = ({ 
  transaction, 
  onClose,
  onViewDetails,
  onDownloadReceipt,
  onPrintReceipt,
  onContactCustomer,
  onFlagTransaction,
  onProcessRefund
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        onClose?.();
      }
    };

    if (isOpen) {
      document?.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleViewDetails = () => {
    onViewDetails?.(transaction);
    setIsOpen(false);
  };

  const handleDownloadReceipt = () => {
    if (!transaction) return;
    
    const receiptContent = `
TRANSACTION RECEIPT
==================

Transaction ID: ${transaction?.id}
Date: ${new Date(transaction?.date)?.toLocaleString()}
Status: ${transaction?.status?.toUpperCase()}

FROM
====
Customer: ${transaction?.customer}
Amount Sent: ${transaction?.sendAmount} ${transaction?.fromCurrency}

TO
==
Recipient: ${transaction?.recipient}
Country: ${transaction?.recipientCountry}
Amount Received: ${transaction?.receiveAmount} ${transaction?.toCurrency}

DETAILS
=======
Exchange Rate: 1 ${transaction?.fromCurrency} = ${transaction?.exchangeRate} ${transaction?.toCurrency}
Transfer Fee: ${transaction?.fee} ${transaction?.fromCurrency}
Payment Method: ${transaction?.paymentMethod}
Reference: ${transaction?.reference}
Commission: $${transaction?.commission}
    `;

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${transaction?.id}.txt`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    window.URL?.revokeObjectURL(url);
    
    setIsOpen(false);
  };

  const handleContactCustomer = () => {
    onContactCustomer?.(transaction);
    setIsOpen(false);
  };

  const handleFlagTransaction = () => {
    onFlagTransaction?.(transaction);
    setIsOpen(false);
  };

  const handleRefund = () => {
    onProcessRefund?.(transaction);
    setIsOpen(false);
  };

  const handlePrintReceipt = () => {
    if (!transaction) return;
    
    const printWindow = window.open('', '_blank');
    printWindow?.document?.write(`
      <html>
        <head>
          <title>Transaction Receipt - ${transaction?.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { font-size: 24px; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; margin-bottom: 10px; }
            .row { display: flex; justify-content: space-between; margin: 5px 0; }
            .label { color: #666; }
            .value { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Transaction Receipt</h1>
          <div class="section">
            <div class="row">
              <span class="label">Transaction ID:</span>
              <span class="value">${transaction?.id}</span>
            </div>
            <div class="row">
              <span class="label">Date:</span>
              <span class="value">${new Date(transaction?.date)?.toLocaleString()}</span>
            </div>
            <div class="row">
              <span class="label">Status:</span>
              <span class="value">${transaction?.status?.toUpperCase()}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Sender Information</div>
            <div class="row">
              <span class="label">Customer:</span>
              <span class="value">${transaction?.customer}</span>
            </div>
            <div class="row">
              <span class="label">Amount Sent:</span>
              <span class="value">${transaction?.sendAmount} ${transaction?.fromCurrency}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Recipient Information</div>
            <div class="row">
              <span class="label">Recipient:</span>
              <span class="value">${transaction?.recipient}</span>
            </div>
            <div class="row">
              <span class="label">Country:</span>
              <span class="value">${transaction?.recipientCountry}</span>
            </div>
            <div class="row">
              <span class="label">Amount Received:</span>
              <span class="value">${transaction?.receiveAmount} ${transaction?.toCurrency}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Transaction Details</div>
            <div class="row">
              <span class="label">Exchange Rate:</span>
              <span class="value">1 ${transaction?.fromCurrency} = ${transaction?.exchangeRate} ${transaction?.toCurrency}</span>
            </div>
            <div class="row">
              <span class="label">Transfer Fee:</span>
              <span class="value">${transaction?.fee} ${transaction?.fromCurrency}</span>
            </div>
            <div class="row">
              <span class="label">Payment Method:</span>
              <span class="value">${transaction?.paymentMethod}</span>
            </div>
            <div class="row">
              <span class="label">Reference:</span>
              <span class="value">${transaction?.reference}</span>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 100);
            };
          </script>
        </body>
      </html>
    `);
    printWindow?.document?.close();
    
    setIsOpen(false);
  };

  const menuItems = [
    {
      label: 'View Details',
      icon: 'Eye',
      onClick: handleViewDetails,
      show: true
    },
    {
      label: 'Download Receipt',
      icon: 'Download',
      onClick: handleDownloadReceipt,
      show: transaction?.status === 'completed'
    },
    {
      label: 'Print Receipt',
      icon: 'Printer',
      onClick: handlePrintReceipt,
      show: transaction?.status === 'completed'
    },
    {
      label: 'Contact Customer',
      icon: 'MessageCircle',
      onClick: handleContactCustomer,
      show: true
    },
    {
      label: 'Flag Transaction',
      icon: 'Flag',
      onClick: handleFlagTransaction,
      show: transaction?.status !== 'failed' && transaction?.status !== 'cancelled',
      className: 'text-warning'
    },
    {
      label: 'Process Refund',
      icon: 'RefreshCcw',
      onClick: handleRefund,
      show: transaction?.status === 'completed',
      className: 'text-error'
    }
  ];

  const visibleItems = menuItems?.filter(item => item?.show);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-muted transition-colors"
        aria-label="Transaction actions"
      >
        <Icon name="MoreVertical" size={18} className="text-muted-foreground" />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
          {visibleItems?.map((item, index) => (
            <React.Fragment key={item?.label}>
              {index > 0 && index === visibleItems?.length - 2 && (
                <div className="my-1 border-t border-border" />
              )}
              <button
                onClick={item?.onClick}
                className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-3 hover:bg-muted transition-colors ${
                  item?.className || 'text-foreground'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionActionsMenu;